// User preference only. Demo identity stays in the existing CID/document ID.
export const demoEnabled = user => user?.status === "active" && user?.role === "admin" && user?.demoMode === true;
export const isDemoId = id => /^(?:DO_)?DEMO[_-]/i.test(String(id || ""));
export const demoCollection = name => name.replace(/^deliverOrders_/, "demoDeliverOrders_").replace(/^containers_/, "demoContainers_").replace(/^locationAddress_/, "demoLocationAddress_").replace(/^deliveryAddress_/, "demoDeliveryAddress_");
export function newDemoCid(branch, uniqueId) {
  if (!["NY", "CHI"].includes(branch) || !/^[a-z0-9-]{10,70}$/i.test(uniqueId)) throw new Error("Invalid demo reference.");
  return `DEMO-${branch}-${uniqueId.toUpperCase()}`;
}

// Only operational reads are joined. Writes always use real SDK references.
// Both queries carry identical filters; failures never silently omit one source.
export function withDemoCollections(db, enabled, onReadError = () => {}) {
  const operational = /^(deliverOrders|containers|locationAddress|deliveryAddress)_(ny|chi)$/;
  function wrap(query, demoQuery, orders = [], cap, inequality) {
    const compare = (a, b) => {
      for (const [field, direction] of orders.length ? orders : [[inequality || "__name__", "asc"]]) {
        const value = doc => field === "__name__" ? doc.id : field.split(".").reduce((v, k) => v?.[k], doc.data());
        const normalize = v => v?.toMillis ? v.toMillis() : v instanceof Date ? v.getTime() : v;
        const av = normalize(value(a)), bv = normalize(value(b));
        if (av !== bv) return (av < bv ? -1 : 1) * (direction === "desc" ? -1 : 1);
      }
      return a.ref.path < b.ref.path ? -1 : a.ref.path > b.ref.path ? 1 : 0;
    };
    const join = snapshots => {
      let docs = snapshots.flatMap(s => s.docs).sort(compare);
      if (cap !== undefined) docs = docs.slice(0, cap);
      return {docs, size: docs.length, empty: docs.length === 0,
        metadata: {fromCache: snapshots.some(s => s.metadata?.fromCache), hasPendingWrites: snapshots.some(s => s.metadata?.hasPendingWrites)},
        forEach: fn => docs.forEach(fn)};
    };
    return new Proxy(query, {get(target, key) {
      if (key === "get") return async (...args) => {
        try {return join(await Promise.all([target.get(...args), demoQuery.get(...args)]));}
        catch (error) {onReadError(error);throw error;}
      };
      if (key === "onSnapshot") return (...args) => {
        const next = typeof args[0] === "function" ? args[0] : args[0]?.next;
        const error = typeof args[0] === "function" ? args[1] : args[0]?.error;
        let stopped = false; const snapshots = []; const unsubs = [];
        const stop = () => {stopped = true;unsubs.forEach(fn => fn());};
        [target, demoQuery].forEach((source, index) => {
          if (!stopped) unsubs.push(source.onSnapshot(s => {if (!stopped) {snapshots[index] = s;if (snapshots[0] && snapshots[1]) next(join(snapshots));}}, e => {stop();onReadError(e);if (error) error(e);}));
        });
        return stop;
      };
      if (["where", "orderBy", "limit", "startAt", "endAt", "startAfter", "endBefore"].includes(key)) return (...args) => wrap(
        target[key](...args), demoQuery[key](...args), key === "orderBy" ? [...orders, [args[0], args[1] || "asc"]] : orders,
        key === "limit" ? args[0] : cap, key === "where" && ["<", "<=", ">", ">=", "!=", "not-in"].includes(args[1]) ? args[0] : inequality);
      const v = Reflect.get(target, key, target);return typeof v === "function" ? v.bind(target) : v;
    }});
  }
  return new Proxy(db, {get(target, key) {
    if (key === "collection") return name => {
      const collection = target.collection(name);
      if (!operational.test(name)) return collection;
      const demo = target.collection(demoCollection(name));
      // Shared address selectors must not offer demo addresses while editing
      // a production DO. Tender review lists isolated addresses explicitly;
      // direct DEMO_ references still resolve for an existing demo DO.
      const addressBook = /^(locationAddress|deliveryAddress)_/.test(name);
      const read = enabled() && !addressBook ? wrap(collection, demo) : collection;
      return new Proxy(read, {get(q, prop) {
        if (prop === "doc") return id => {
          if (isDemoId(id)) {
            if (!enabled()) throw new Error("Enable Demo mode as an active admin to access this record.");
            return demo.doc(id);
          }
          return id === undefined ? collection.doc() : collection.doc(id);
        };
        const v = Reflect.get(q, prop, q);return typeof v === "function" ? v.bind(q) : v;
      }});
    };
    const v = Reflect.get(target, key, target);return typeof v === "function" ? v.bind(target) : v;
  }});
}

// Old deployed approval handlers reject this ID-free probe before any writes.
// Never send a demo approval to a handler that would ignore the demo option.
export async function requireDemoSupport(api) {
  try {
    const response = await api.httpsCallable("decideChrTender")({action: "capabilities"});
    if (response.data?.demoModeSupported !== true) throw new Error("Unsupported backend");
  } catch {
    throw new Error("Demo mode is not ready. Deploy the demo Firestore rules/indexes and Functions before enabling it.");
  }
}
